import React, { useState } from "react";
import { ButtonComp, CardComp, ModalCompAdvance } from "../../../../components";
import { useGetMemberLoanRequestDetailsQuery } from "../../../../store/member/member-loan";
import { AiOutlineClose } from "react-icons/ai";
import { formatMoney } from "../../../../utils";
import moment from "moment";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { convertToTitleCase } from "../../../../utils/helpers";
import { methodLabel } from "../../../../components/Loan/loanTypeModel";

const MemberViewLoanDetailsModal = ({ requestId, setModalPage }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const {
    data: loanDetails,
    isLoading,
    isError,
  } = useGetMemberLoanRequestDetailsQuery(requestId, {
    skip: !requestId,
  });

  const loan = loanDetails || {};

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const imageModal = [
    {
      name: "view_image",
      component: (
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">Guarantor Photo</h5>
            <AiOutlineClose
              onClick={() => setSelectedImage(null)}
              className="cursor-pointer"
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
          </div>
          <div className="text-center">
            <img
              src={selectedImage}
              alt="Guarantor"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "10px",
              }}
              onError={(e) => {
                e.target.src =
                  "https://res.cloudinary.com/dammymoses/image/upload/v1654672815/avatars/avatar_wv2acz.svg";
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="relative p-4">
        <AiOutlineClose
          onClick={() => setModalPage()}
          className="absolute top-4 right-4 cursor-pointer"
          size={24}
        />
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="relative p-4">
        <AiOutlineClose
          onClick={() => setModalPage()}
          className="absolute top-4 right-4 cursor-pointer"
          size={24}
        />
        <div className="text-center py-5">
          <p className="text-danger">Failed to load loan details</p>
          {isError && (
            <small className="text-muted">
              Error: {isError?.data?.message || isError?.message || "Unknown error"}
            </small>
          )}
        </div>
      </div>
    );
  }

  if (!isLoading && (!loan || !loan.id)) {
    return (
      <div className="relative p-4">
        <AiOutlineClose
          onClick={() => setModalPage()}
          className="absolute top-4 right-4 cursor-pointer"
          size={24}
        />
        <div className="text-center py-5">
          <p className="text-muted">No loan data available</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative p-4"
      style={{ maxHeight: "90vh", overflowY: "auto" }}
    >
      <AiOutlineClose
        onClick={() => setModalPage()}
        className="absolute top-4 right-4 cursor-pointer"
        size={24}
        style={{ color: "var(--color-primary)" }}
      />
      <h4 className="border-b fw-bold py-2 mb-4 text-[1.5rem] text-primary1 text-center w-full">
        Loan Details
      </h4>

      <div className="row g-3">
        {/* Loan Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Loan Information</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Loan Type</small>
                  <span className="fw-bold">
                    {loan?.loan_type?.name || "N/A"}
                  </span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Loan Amount</small>
                  <span className="fw-bold text-primary1">
                    {formatMoney(loan?.amount || 0)}
                  </span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">
                    Repayment Plan
                  </small>
                  <span className="fw-bold">
                    {convertToTitleCase(loan?.repayment_plan || "N/A")}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Duration</small>
                  <span className="fw-bold">
                    {loan?.duration || "N/A"}{" "}
                    {convertToTitleCase(loan?.duration_type || "")}
                  </span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Status</small>
                  <div
                    className="px-3 py-1 rounded-pill text-white d-inline-block"
                    style={{
                      backgroundColor:
                        loan?.status === "PENDING"
                          ? "#FFA500"
                          : loan?.status === "APPROVED"
                          ? "#19BC31"
                          : loan?.status === "DECLINED"
                          ? "#FF4343"
                          : loan?.status === "DISBURSED"
                          ? "var(--color-secondary)"
                          : "#A1A0A0",
                    }}
                  >
                    {convertToTitleCase(loan?.status || "UNKNOWN")}
                  </div>
                </div>
                {loan?.decline_reason && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">
                      Decline Reason
                    </small>
                    <span className="text-danger">{loan?.decline_reason}</span>
                  </div>
                )}
              </div>
            </div>
            {/* Only show payback date if loan has been disbursed */}
            {loan?.status === "DISBURSED" && loan?.payback_at && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-muted d-block mb-1">Payback Date</small>
                <span className="fw-bold">
                  {moment(loan?.payback_at).format("DD-MMM-YYYY")}
                </span>
              </div>
            )}
          </CardComp>
        </div>

        {/* Guarantor Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Guarantor Details</h5>
            {loan?.guarantors && loan.guarantors.length > 0 ? (
              <div className="row g-3">
                {loan.guarantors.map((guarantor, index) => {
                  const guarantorPhoto =
                    guarantor?.photo ||
                    guarantor?.member?.photo ||
                    "https://res.cloudinary.com/dammymoses/image/upload/v1654672815/avatars/avatar_wv2acz.svg";

                  return (
                    <div key={index} className="col-md-6 mb-3">
                      <div className="border rounded p-3">
                        <div className="d-flex align-items-center mb-2">
                          <div
                            className="me-3 cursor-pointer"
                            onClick={() => handleImageClick(guarantorPhoto)}
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "50%",
                              overflow: "hidden",
                              border: "2px solid #e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "var(--color-primary)";
                              e.currentTarget.style.transform = "scale(1.05)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e0e0e0";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                            title="Click to view full image"
                          >
                            <FallbackImage
                              src={guarantorPhoto}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0 fw-bold">{guarantor?.name}</h6>
                            <div
                              className={`px-2 py-1 rounded-pill text-white d-inline-block mt-1`}
                              style={{
                                fontSize: "0.75rem",
                                backgroundColor:
                                  guarantor?.status === "CONFIRMED"
                                    ? "#19BC31"
                                    : guarantor?.status === "DECLINED"
                                    ? "#FF4343"
                                    : "#FFA500",
                              }}
                            >
                              {convertToTitleCase(guarantor?.status || "PENDING")}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          {guarantor?.email && (
                            <div className="mb-1">
                              <small className="text-muted">Email: </small>
                              <span>{guarantor.email}</span>
                            </div>
                          )}
                          {guarantor?.phone && (
                            <div className="mb-1">
                              <small className="text-muted">Phone: </small>
                              <span>{guarantor.phone}</span>
                            </div>
                          )}
                          {guarantor?.occupation && (
                            <div className="mb-1">
                              <small className="text-muted">Occupation: </small>
                              <span>{guarantor.occupation}</span>
                            </div>
                          )}
                          {guarantor?.home_address && (
                            <div className="mb-1">
                              <small className="text-muted">Address: </small>
                              <span>{guarantor.home_address}</span>
                            </div>
                          )}
                          {guarantor?.member?.member_no && (
                            <div className="mb-1">
                              <small className="text-muted">
                                Membership Number:{" "}
                              </small>
                              <span>{guarantor.member.member_no}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted">No guarantors assigned</p>
            )}
          </CardComp>
        </div>

        {/* Loan Type Details */}
        {loan?.loan_type && (
          <div className="col-12">
            <CardComp cardClassName="border-0 shadow-sm">
              <h5 className="fw-bold mb-3 text-primary1">Loan Type Details</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="mb-2">
                    <small className="text-muted d-block">Interest Rate</small>
                    <span className="fw-bold">
                      {loan.loan_type.rate || "N/A"}%
                    </span>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      Interest Calculation Mode
                    </small>
                    <span className="fw-bold">
                      {methodLabel(loan.loan_type.interest_calculation_mode) ||
                        convertToTitleCase(
                          loan.loan_type.interest_calculation_mode || "N/A"
                        )}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-2">
                    <small className="text-muted d-block">Amount Range</small>
                    <span className="fw-bold">
                      {formatMoney(loan.loan_type.min_loan_amount || 0)} -{" "}
                      {formatMoney(loan.loan_type.max_loan_amount || 0)}
                    </span>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      Guarantors Required
                    </small>
                    <span className="fw-bold">
                      {loan.loan_type.min_guarantor || "N/A"} -{" "}
                      {loan.loan_type.max_guarantor || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </CardComp>
          </div>
        )}

        {/* Additional Information */}
        <div className="col-12">
          <CardComp cardClassName="border-0 shadow-sm">
            <h5 className="fw-bold mb-3 text-primary1">Additional Information</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-2">
                  <small className="text-muted d-block">Date Applied</small>
                  <span>
                    {loan?.created_at
                      ? moment(loan.created_at).format("DD-MMM-YYYY HH:mm")
                      : "N/A"}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-2">
                  <small className="text-muted d-block">Last Updated</small>
                  <span>
                    {loan?.updated_at
                      ? moment(loan.updated_at).format("DD-MMM-YYYY HH:mm")
                      : "N/A"}
                  </span>
                </div>
              </div>
              {loan?.disbursed_at && (
                <div className="col-md-6">
                  <div className="mb-2">
                    <small className="text-muted d-block">Disbursed Date</small>
                    <span>
                      {moment(loan.disbursed_at).format("DD-MMM-YYYY")}
                    </span>
                  </div>
                </div>
              )}
              {loan?.disbursement_description && (
                <div className="col-12">
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      Disbursement Description
                    </small>
                    <p>{loan.disbursement_description}</p>
                  </div>
                </div>
              )}
            </div>
          </CardComp>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <ButtonComp
          btnText={"Close"}
          btnClassName="bg-primary1 text-white px-4 py-2 rounded"
          onClick={() => setModalPage()}
        />
      </div>

      {/* Image View Modal */}
      <ModalCompAdvance
        modalBody={0}
        show={!!selectedImage}
        arrayComp={imageModal}
        handleClose={() => setSelectedImage(null)}
        centered
        size={"lg"}
      />
    </div>
  );
};

export default MemberViewLoanDetailsModal;

